import { monthNames } from "../../consts/common";
import { arrayHasItems } from "../../helpers/common";
import {
    getCompareYtdColumnConfig,
    getCompareYtdFieldValue,
    hasCompareYtdTotalsForTable,
    isCompareYtdTotalsEntry,
    isPercentageMetricName,
} from "../../helpers/profitAndLoss/compareYtd";
import {
    getProfitAndLossVarianceDirection,
    isVarianceImprovement,
} from "../../helpers/profitAndLoss/varianceDirection";
import { formatAmount, formatPercent } from "../../helpers/quickbooks";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { LoaderSpinner } from "../LoaderSpinner";
import { useEffect, useRef } from "react";
import { useIsMobile } from "../../helpers/useIsMobile";

export const ProfitLossTable = ({
    data,
    title,
    acceptSidebarData,
    totalsByRows,
    className = "",
    type,
    compareType,
    onExport,
    isExporting = false,
    tableKey,
    compact = false
}) => {
    const isMobile = useIsMobile();
    const months = Object.keys(data || {});
    const headerCellPadding = compact ? "px-3 py-2" : "px-6 py-3";
    const bodyCellPadding = compact ? "px-3 py-2" : "px-6 py-4";
    const tableWidthClass = compact ? (isMobile ? "w-max" : "w-max min-w-full") : "w-full";
    const tableStyle = compact ? { tableLayout: "fixed" } : undefined;
    const metricColumnStyle = compact
        ? { width: "200px", minWidth: "200px", maxWidth: "200px" }
        : undefined;
    const numericColumnStyle = compact
        ? { width: "122px", minWidth: "122px", maxWidth: "122px" }
        : undefined;
    const transformedData = {};
    const isCompareType = compareType && compareType !== "none";
    const isCompareLayout = isCompareType && months.length >= 3 && months.length % 3 === 0;
    const isBudgetCompare = String(compareType || "").startsWith("budget-");

    // In compare mode the first column can be "Prev" and sometimes has no data (e.g. missing prior year),
    // so we must build the metric list as a union across all columns.
    const metricSet = new Set();
    for (const m of months) {
      for (const key of Object.keys((data || {})[m] || {})) metricSet.add(key);
    }
    const allMetrics = Array.from(metricSet);

    const getMetricPos = (metricName) => {
      for (const m of months) {
        const pos = (data || {})?.[m]?.[metricName]?.position;
        const p = Number(pos);
        if (Number.isFinite(p) && p > 0) return p;
      }
      return 0;
    };

    allMetrics.sort((a, b) => {
      const pa = getMetricPos(a);
      const pb = getMetricPos(b);
      if (pa !== pb) return pa - pb;
      return String(a).localeCompare(String(b));
    });

    allMetrics.forEach((metric) => {
      transformedData[metric] = months.map((month) => ({
        month,
        value: data[month]?.[metric] ?? null,
      }));
    });
    // NOTE: We intentionally keep the legacy formatting semantics: 0 => "-" for both amounts and percents.
  
    // JS-driven sticky first column (simulate sticky without CSS)
    const scrollContainerRef = useRef(null);
    const stickyHeaderRef = useRef(null);
    const stickyCellsRef = useRef([]);
    const leftGutterRef = useRef(null);

    const ensureDivider = (el, color = "rgba(229,231,235,1)") => {
      if (!el) return;
      let divider = el.querySelector('[data-sticky-divider-el="true"]');
      if (!divider) {
        divider = document.createElement("span");
        divider.setAttribute("data-sticky-divider-el", "true");
        divider.setAttribute("aria-hidden", "true");
        divider.style.position = "absolute";
        divider.style.top = "0";
        divider.style.bottom = "0";
        divider.style.right = "0";
        divider.style.width = "1px";
        divider.style.pointerEvents = "none";
        el.appendChild(divider);
      }
      divider.style.backgroundColor = color;
    };

    const setStickyCellRef = (el) => {
      if (el) stickyCellsRef.current.push(el);
    };

    const applyStickyTransforms = () => {
      if (isMobile) return;
      const scroller = scrollContainerRef.current;
      if (!scroller) return;
      const x = scroller.scrollLeft || 0;

      // Expand left gutter to cover revealed space when cells translate
      if (leftGutterRef.current) {
        leftGutterRef.current.style.width = `${x}px`;
      }

      if (stickyHeaderRef.current) {
        const th = stickyHeaderRef.current;
        th.style.transform = `translateX(${x}px)`;
        th.style.position = "relative";
        th.style.zIndex = 3;
        // Header background and delimiter
        th.style.setProperty("background-color", "rgb(249 250 251)", "important");
        ensureDivider(th, "rgba(229,231,235,1)");
      }

      for (const td of stickyCellsRef.current) {
        td.style.transform = `translateX(${x}px)`;
        td.style.position = "relative";
        td.style.zIndex = 2;
        // Delimiter for the sticky column (pseudo-like element)
        const isPercentageRow = td.classList?.contains("bg-gray-50") || td.parentElement?.className?.includes("bg-gray-50");
        ensureDivider(td, "rgba(229,231,235,1)");

        // Background color based on classnames
        const bgColor = isPercentageRow ? "#f9fafb" : "#ffffff";
        td.style.setProperty("background-color", bgColor, "important");
      }
    };

    const lockFirstColumnWidth = () => {
      if (isMobile) return;
      const th = stickyHeaderRef.current;
      if (!th) return;
      const measured = th.offsetWidth || 0;
      if (!measured) return;
      const MIN_W = 160;
      const MAX_W = 240;
      const width = Math.max(MIN_W, Math.min(measured, MAX_W));
      th.style.minWidth = `${width}px`;
      th.style.maxWidth = `${width}px`;
      th.style.overflow = "hidden";
      th.style.textOverflow = "ellipsis";
      th.style.whiteSpace = "nowrap";
      for (const td of stickyCellsRef.current) {
        td.style.minWidth = `${width}px`;
        td.style.maxWidth = `${width}px`;
        td.style.overflow = "hidden";
        td.style.textOverflow = "ellipsis";
        td.style.whiteSpace = "nowrap";
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      const scroller = scrollContainerRef.current;
      if (!scroller) return;

      if (isMobile) {
        const stickyElements = [
          stickyHeaderRef.current,
          ...stickyCellsRef.current,
        ].filter(Boolean);
        for (const element of stickyElements) {
          element.style.removeProperty("transform");
          element.style.removeProperty("position");
          element.style.removeProperty("z-index");
          element.style.removeProperty("min-width");
          element.style.removeProperty("max-width");
          element.style.removeProperty("overflow");
          element.style.removeProperty("text-overflow");
          element.style.removeProperty("white-space");
          element.style.removeProperty("background-color");
          element.querySelector('[data-sticky-divider-el="true"]')?.remove();
        }
        if (leftGutterRef.current) leftGutterRef.current.style.width = "0";
        return;
      }

      // allow next paint to capture sizes
      requestAnimationFrame(() => {
        lockFirstColumnWidth();
        applyStickyTransforms();
      });

      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          applyStickyTransforms();
          ticking = false;
        });
      };

      const onResize = () => {
        lockFirstColumnWidth();
        applyStickyTransforms();
      };

      scroller.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onResize, { passive: true });
      return () => {
        scroller.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onResize);
      };
    }, [allMetrics.length, isMobile, months.length, type]);

    // Reset refs collection before mapping rows
    stickyCellsRef.current = [];

    const shouldShowCompareYtdCols = isCompareType && hasCompareYtdTotalsForTable(totalsByRows, tableKey);
    const compareYtdColumns = getCompareYtdColumnConfig(compareType);

    if (!data) return null;

    const formatMonthLabel = (monthKey, index) => {
      if (!monthKey) return "";
      if (isCompareLayout) {
        // Every 3rd col is variance for the current month bucket
        if (index % 3 === 2) return "Variance";
      }
      if (String(monthKey).endsWith(" Var")) return "Variance";
      return monthKey;
    };

    const shouldShowTotalCol = !isCompareType && !!totalsByRows && !!totalsByRows?.[tableKey];

    const getVarianceColorClass = (numericValue, direction) => {
        return isVarianceImprovement(numericValue, direction)
            ? "text-emerald-600"
            : "text-red-500";
    };

    const renderYtdCell = (metric, column, isPercentageMetric) => {
        const hideSymbol = metric === "BEROAS" || metric === "MER";
        const ytdEntry = totalsByRows?.[tableKey]?.[metric];
        const ytdValue = getCompareYtdFieldValue(ytdEntry, column.field);
        const isVarianceCol = Boolean(column?.isVariance);
        const isPct = isCompareYtdTotalsEntry(ytdEntry)
            ? Boolean(ytdEntry?.isPercentage)
            : isPercentageMetric;
        const isGrowthMetric = String(metric).toLowerCase().includes("yoy growth");
        const hasValue = ytdValue !== null && ytdValue !== undefined;
        const numericValue = Number(ytdValue);
        const varianceDirection = getProfitAndLossVarianceDirection(tableKey, metric);
        const isNegative = hasValue && !Number.isNaN(numericValue) && numericValue < 0;
        const displayValue = !hasValue
            ? "-"
            : isPct
                ? formatPercent(numericValue, hideSymbol)
                : formatAmount(numericValue, hideSymbol);

        return (
            <td
                key={`ytd_${column.field}_${metric}`}
                style={numericColumnStyle}
                className={`${bodyCellPadding} whitespace-nowrap text-right text-sm tabular-nums border-l border-gray-200 ${
                    isVarianceCol
                        ? "bg-gray-50"
                        : isPct
                            ? "bg-gray-50"
                            : "bg-white"
                }`}
            >
                <div
                    className={`text-sm font-bold ${
                        !hasValue
                            ? "text-gray-400"
                            : isVarianceCol
                                ? getVarianceColorClass(numericValue, varianceDirection)
                                : isNegative
                                ? "text-red-500"
                                : isPct && isGrowthMetric
                                    ? "text-emerald-600"
                                : isPct
                                    ? "text-blue-600"
                                    : "text-stone-900"
                    }`}
                >
                    {displayValue}
                </div>
            </td>
        );
    };

    return (
      <div className={`bg-white overflow-hidden ${className}`}>
        <div className="border-b border-gray-200 px-6 flex justify-between items-center">
          <div className={`py-3 ${compact ? "-ml-3" : ""}`}>
            <div className="flex items-center gap-2 h-[32px]">
              <h3 className="text-base font-bold text-stone-900">{title}</h3>
            </div>
          </div>
          <div className="py-2">
            <button 
              onClick={() => onExport?.(tableKey)}
              disabled={isExporting}
              className="flex items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-stone-900 transition-all duration-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <>
                  <LoaderSpinner width="16" height="16" border="2" parentHeight="h-4" />
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Export</span>
                </>
              )}
            </button>
          </div>
        </div>
        <div className="min-w-0 max-w-full overflow-x-auto" ref={scrollContainerRef} style={{ position: "relative" }}>
          <div ref={leftGutterRef} style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 0, backgroundColor: "#fff", zIndex: 1, pointerEvents: "none" }} />
          <table className={tableWidthClass} style={tableStyle}>
            {compact && (
              <colgroup>
                <col style={metricColumnStyle} />
                {months.map((month, index) => <col key={`month_col_${month}_${index}`} style={numericColumnStyle} />)}
                {shouldShowTotalCol && <col style={numericColumnStyle} />}
                {shouldShowCompareYtdCols && compareYtdColumns.map((column) => (
                  <col key={`ytd_col_${column.field}`} style={numericColumnStyle} />
                ))}
              </colgroup>
            )}
            <thead>
              {isCompareLayout && isBudgetCompare ? (
                <>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th
                      ref={stickyHeaderRef}
                      rowSpan={2}
                      style={metricColumnStyle}
                      className={`${headerCellPadding} text-left text-xs font-medium text-zinc-500 uppercase tracking-wider`}
                    >
                      Metric
                    </th>
                    {(() => {
                      const groups = [];
                      for (let i = 0; i < months.length; i += 3) {
                        const actualKey = months[i];
                        if (!actualKey) break;
                        // actualKey like "Oct 25"
                        const [m, yy] = String(actualKey || "").split(" ");
                        const label = yy ? `${String(m || "").toUpperCase()} '${yy}` : String(actualKey || "").toUpperCase();
                        groups.push({ label, index: i });
                      }
                      return groups.map((g, gi) => (
                        <th
                          key={`group_${gi}`}
                          colSpan={3}
                          className={`${headerCellPadding} text-right text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-gray-200`}
                        >
                          {g.label}
                        </th>
                      ));
                    })()}
                    {shouldShowCompareYtdCols && (
                      <>
                        {compareYtdColumns.map((column) => (
                          <th
                            key={`ytd_header_${column.field}`}
                            rowSpan={2}
                            style={numericColumnStyle}
                            className={`${headerCellPadding} text-right text-xs font-medium text-zinc-500 uppercase tracking-wider border-l border-gray-200 bg-gray-50`}
                          >
                            {column.label}
                          </th>
                        ))}
                      </>
                    )}
                  </tr>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {months.map((_, index) => {
                      const subIndex = index % 3;
                      const label = subIndex === 0 ? "Actuals" : subIndex === 1 ? "Forecast" : "Variance";
                      const isForecastCol = subIndex === 1;
                      const isEndGroup = subIndex === 2;
                      return (
                        <th
                          key={`sub_${index}`}
                          style={numericColumnStyle}
                          className={`${headerCellPadding} text-right text-xs font-medium text-zinc-500 uppercase tracking-wider ${
                            isForecastCol ? "border-l border-r border-gray-200" : ""
                          } ${isEndGroup ? "border-r border-gray-200 bg-gray-50" : ""}`}
                        >
                          {label}
                        </th>
                      );
                    })}
                  </tr>
                </>
              ) : (
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th
                    ref={stickyHeaderRef}
                    style={metricColumnStyle}
                    className={`${headerCellPadding} text-left text-xs font-medium text-zinc-500 uppercase tracking-wider`}
                  >
                    Metric
                  </th>
                  {months.length > 0 && months.map((month, index) => {
                      const isVarianceCol = isCompareLayout ? index % 3 === 2 : String(month || "").endsWith(" Var");
                      const label = formatMonthLabel(month, index).replace("PY", "");
                      return (
                        <th
                          key={index}
                          style={numericColumnStyle}
                          className={`${headerCellPadding} text-right text-xs font-medium text-zinc-500 uppercase tracking-wider ${
                            isVarianceCol ? "bg-gray-50 border-l border-gray-200" : ""
                          } ${(isCompareLayout && index % 3 === 2) ? "border-r border-gray-200" : ""}`}
                        >
                          {label}
                        </th>
                      )
                  })}
                  {shouldShowTotalCol && (
                    <th
                      className={`${headerCellPadding} text-right text-xs font-medium text-zinc-500 uppercase tracking-wider ${isMobile ? "" : "sticky right-0 z-40"} bg-gray-50 border-l border-gray-200`}
                      style={isMobile
                        ? numericColumnStyle
                        : { ...numericColumnStyle, position: "sticky", right: 0, boxShadow: "inset 1px 0 0 rgba(229, 231, 235, 1)" }}
                    >
                      Total
                    </th>
                  )}
                  {shouldShowCompareYtdCols && (
                    <>
                      {compareYtdColumns.map((column) => (
                        <th
                          key={`ytd_header_${column.field}`}
                          style={numericColumnStyle}
                          className={`${headerCellPadding} text-right text-xs font-medium text-zinc-500 uppercase tracking-wider border-l border-gray-200 bg-gray-50`}
                        >
                          {column.label}
                        </th>
                      ))}
                    </>
                  )}
                </tr>
              )}
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {Object.entries(transformedData).map(([metric, values], index) => {
                const isPercentageMetric = isPercentageMetricName(metric);
                const isGrowthMetric = String(metric).toLowerCase().includes("yoy growth");
                return (
                  <tr key={index} className={`hover:bg-gray-50 transition-colors ${
                    isPercentageMetric ? "bg-gray-50" : "bg-white"
                  }`}>
                    <td ref={setStickyCellRef} style={metricColumnStyle} className={`${bodyCellPadding} whitespace-nowrap`}>
                      <div title={metric} className={`text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis ${
                          isPercentageMetric ? "text-zinc-500" : "text-stone-900"
                        }`}>{isPercentageMetric && <span className="text-zinc-400 mr-2">%</span>}{metric}</div>
                    </td>
                    {values.map(({ month, value: item }, valueIndex) => {
                      const hideSymbol = metric === "BEROAS" || metric === "MER";
                      const cellValue = item?.isPercentage
                        ? formatPercent(item?.value, hideSymbol)
                        : formatAmount(item?.value, hideSymbol);
                      const numericValue = Number(item?.value);
                      const isNegative = !Number.isNaN(numericValue) && numericValue < 0
                      const hasItems = arrayHasItems(item?.items);
                      const isVarianceCol = isCompareLayout ? valueIndex % 3 === 2 : String(month || "").endsWith(" Var");
                      return (
                        <td 
                          key={`${item?.value}_${valueIndex}`} 
                          style={numericColumnStyle}
                          className={`${bodyCellPadding} whitespace-nowrap text-right text-sm tabular-nums ${
                            isVarianceCol ? "bg-gray-50 border-l border-gray-200" : ""
                          } ${(isCompareLayout && valueIndex % 3 === 2) ? "border-r border-gray-200" : ""} ${hasItems ? "hover:bg-gray-50 cursor-pointer" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (arrayHasItems(item?.items)) {
                              acceptSidebarData?.({
                                total: item?.value,
                                items: item?.items,
                                title: `${metric} Breakdown`,
                                month: `${monthNames[month.split(" ")[0]]} ${month.split(" ")[1]}`
                              })
                            }
                          }}
                        >
                          <div
                            className={`text-sm font-medium ${
                              isVarianceCol
                                ? getVarianceColorClass(
                                    Number(item?.value || 0),
                                    getProfitAndLossVarianceDirection(tableKey, metric)
                                  )
                                : isNegative
                                  ? "text-red-500"
                                  : item?.isPercentage && isGrowthMetric
                                    ? "text-emerald-600"
                                    : item?.isPercentage
                                      ? "text-blue-600"
                                      : "text-stone-900"
                            }`}
                          >
                            {cellValue}
                          </div>
                        </td>
                      )
                    })}
                    {shouldShowTotalCol && (
                      (() => {
                        const hideSymbol = metric === "BEROAS" || metric === "MER";
                        const totalRaw = totalsByRows?.[tableKey]?.[metric];
                        const totalIsNegative = !Number.isNaN(Number(totalRaw)) && Number(totalRaw) < 0;
                        const isGrowthMetric = String(metric).toLowerCase().includes("yoy growth");
                        const totalDisplay = isPercentageMetric 
                          ? formatPercent(Number(totalRaw || 0), hideSymbol) 
                          : formatAmount(Number(totalRaw || 0), hideSymbol);
                        return (
                          <td
                            key={`total_${metric}`}
                            className={`${bodyCellPadding} whitespace-nowrap text-right text-sm tabular-nums ${isMobile ? "" : "sticky right-0 z-30"} ${isPercentageMetric ? "bg-gray-50" : "bg-white"} border-l border-gray-200`}
                            style={isMobile
                              ? numericColumnStyle
                              : { ...numericColumnStyle, position: "sticky", right: 0, boxShadow: "inset 1px 0 0 rgba(229, 231, 235, 1)" }}
                          >
                            <div
                              className={`text-sm font-bold ${
                                totalIsNegative
                                  ? "text-red-500"
                                  : isPercentageMetric && isGrowthMetric
                                    ? "text-emerald-600"
                                  : isPercentageMetric
                                    ? "text-blue-600"
                                    : "text-stone-900"
                              }`}
                            >
                              {totalDisplay}
                            </div>
                          </td>
                        );
                      })()
                    )}
                    {shouldShowCompareYtdCols && (
                      <>
                        {compareYtdColumns.map((column) =>
                          renderYtdCell(metric, column, isPercentageMetric)
                        )}
                      </>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
};